import * as _ from 'lodash';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { MutableRefObject, useContext, useEffect, useReducer, useState } from 'react';
import { IRecipientActivityLog } from '@nkr/models/recipient-activity-log';
import { IApplicationState, AppActionCreators, RecipientActionCreators, IRecipientActivityLogResponse, IRecipientXmResponse } from '@nkr/store/index';
import { CsvExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { RecipientProfileContext } from '../models';
import { activityLogReducer, initialState } from './state';
import { ProfileWrapper } from '../styles';
import { Panel } from 'react-bootstrap';
import ActivityLog from './activity-log';
import Filter from './filter';

const Risk = {
    NoData: -1,
    Zero: 0,
    Low: 10,
    Intermediate: 20,
    High: 30
};

interface IProps {
    alias: string,
    saveRef: MutableRefObject<any>
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getActivityLog: (centerId: number, recipientId: number) => Promise<IRecipientActivityLogResponse>,
    getXmRecords: (centerId: number, recipientId: number) => Promise<IRecipientXmResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const XmActivityLog = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(activityLogReducer, initialState);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        props.showLoading("Getting activity log, one moment please....").then(() => {
            Promise.all([
                props.getActivityLog(context.centerId, context.recipientId),
                props.getXmRecords(context.centerId, context.recipientId)
            ])
                .then(([dataResponse, xmResponse]) => {
                    if (dataResponse.data.success && xmResponse.data.success) {
                        dispatch({
                            type: 'afterLoad',
                            value: {
                                activityLogData: dataResponse.data.results,
                                xmData: xmResponse.data.results,
                            }
                        });
                    }
                })
                .catch(() => {

                }).finally(() => {
                    props.hideLoading();
                });
        });
    };

    const onGridReady = (event: GridReadyEvent) => setGridApi(event.api);

    const getFilteredList = (list: IRecipientActivityLog[]) => {
        let startDate = moment(state.startDate);
        let endDate = moment(state.endDate).add(23, 'hours').add(59, 'minutes');

        let filteredList = list.filter(log => {
            let activityFilter = (state.activityTypeId == 0 ? true : log.activityId == state.activityTypeId);
            let donorAliasFilter = state.donorAlias == '' ? true : log.potentialDonor.toLowerCase().includes(state.donorAlias.trim().toLowerCase());
            let dateFilter = (moment(log.activityDate).isSameOrAfter(startDate) && moment(log.activityDate).isSameOrBefore(endDate));

            return activityFilter && donorAliasFilter && dateFilter;
        });

        return filteredList;
    };

    const handleExport = () => {
        let alias = props.alias;

        let params: CsvExportParams = {
            columnGroups: true,
            fileName: `${alias}-activity-log.csv`,
            processCellCallback: (params: ProcessCellForExportParams) => {
                let data = params.node.data as IRecipientActivityLog;

                if (params.column.getColId() == 'singleEpletMmDr') {
                    let { singleEpletMmDr, singleEpletMmDq } = data;
                    let displayLabel = '-?-';

                    if (singleEpletMmDr != null && singleEpletMmDq != null) {
                        if (singleEpletMmDr == 0 && singleEpletMmDq == 0) {
                            displayLabel = '0 MM';
                        } else if (singleEpletMmDr < 7 && singleEpletMmDq < 9) {
                            displayLabel = 'Low';
                        } else if (singleEpletMmDq >= 15) {
                            displayLabel = 'High';
                        } else {
                            displayLabel = 'Med';
                        }
                    }

                    return displayLabel;
                }

                return params.value;
            }
        };

        gridApi.exportDataAsCsv(params);
    }

    const handleSearchFieldChange = (field: string, value: any) => {
        dispatch({
            type: field + 'Change',
            value
        });
    };

    if (context.headerInfo == null)
        return null;

    var activityList = getFilteredList(state.activityLogData);

    return (
        <ProfileWrapper>
            <Panel>
                <Panel.Body style={{ padding: 5 }}>
                    <Filter
                        activityTypeId={state.activityTypeId}
                        startDate={state.startDate}
                        endDate={state.endDate}
                        donorAlias={state.donorAlias}
                        onFieldChange={handleSearchFieldChange}
                        onExportCSV={handleExport}
                    />

                    <ActivityLog
                        data={activityList}
                        onGridReady={onGridReady}
                    />
                </Panel.Body>
            </Panel>
        </ProfileWrapper>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {};
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getActivityLog: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getActivityLog(centerId, recipientId));
        },
        getXmRecords: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getXmRecords(centerId, recipientId))
        }
    };
};

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(XmActivityLog);
